import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@predoc/cow';
import {
  AlertCircle,
  Check,
  Clock,
  ExternalLink,
  Info,
  LoaderCircle,
  X,
} from 'lucide-react';
import { isEfaxError, type AnonymousRecordUploadStatus } from '../types';

const STATUS_CONFIG: Record<
  AnonymousRecordUploadStatus,
  {
    label: string;
    icon: typeof Check;
    className: string;
  }
> = {
  identifying: {
    label: 'Identifying',
    icon: Clock,
    className:
      'border-transparent bg-secondary text-secondary-foreground px-2 py-0.5 font-medium',
  },
  processing: {
    label: 'Processing',
    icon: LoaderCircle,
    className:
      'border-transparent bg-cyan-300 text-foreground px-2 py-0.5 font-medium',
  },
  completed: {
    label: 'Completed',
    icon: Check,
    className:
      'border-transparent bg-green-400 text-foreground px-2 py-0.5 font-medium',
  },
  error: {
    label: 'Error',
    icon: X,
    className:
      'border-transparent bg-destructive text-white px-2 py-0.5 font-medium',
  },
};

const FALLBACK_ERROR_DETAILS =
  'Error: Unable to complete identification or processing of this document.';

interface EfaxStatusBadgeProps {
  status: AnonymousRecordUploadStatus;
  details?: string;
  onViewRawDocument?: () => void;
}

export const EfaxStatusBadge = ({
  status,
  details,
  onViewRawDocument,
}: EfaxStatusBadgeProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  const errorDetails = details?.trim() ? details : FALLBACK_ERROR_DETAILS;

  return (
    <div className="flex items-center gap-1.5">
      <Badge variant="default" className={config.className}>
        <Icon className="size-3" />
        {config.label}
      </Badge>
      {isEfaxError(status) ? (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="View error details"
            >
              <Info className="text-muted-foreground size-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="border-0">
            <DialogHeader>
              <DialogTitle>eFax Processing Error</DialogTitle>
              <DialogDescription className="sr-only">
                {errorDetails}
              </DialogDescription>
            </DialogHeader>
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>{errorDetails}</AlertDescription>
            </Alert>
            <DialogFooter>
              {onViewRawDocument ? (
                <Button
                  type="button"
                  variant="link"
                  className="mr-auto px-0"
                  onClick={() => {
                    setDialogOpen(false);
                    onViewRawDocument();
                  }}
                >
                  <ExternalLink className="size-4" />
                  View original faxed PDF
                </Button>
              ) : null}
              <DialogClose asChild>
                <Button variant="outline">Close</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
};
